// src/features/apps/components/AppBuildRequestCard.tsx

import { useMemo, useState } from 'react';
import { Button, Card, Group, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconHammer } from '@tabler/icons-react';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import type { FactoryStoreKey } from '@/lib/constants/appFactory';
import type { FactoryBuild, FactoryRelease, FactorySubmission } from '@/lib/types/AppFactory';
import { ApiError } from '@/shared/api/ApiError';
import { AppBuildRequestUtils } from '../AppBuildRequestUtils';
import { AppFactoryUtils } from '../AppFactoryUtils';
import { useIsFactoryOperator, useRequestBuild } from '../useAppFactory';

interface AppBuildRequestCardProps {
  appCfgId: number;
  /** The factory platform to build ('roku', 'firetv'). */
  platform: string;
  /** "Fire TV" / "Roku" — the noun the sentences use. */
  platformLabel: string;
  /** The parent app is Active. */
  isAppActive: boolean;
  /** The platform's own row is Active. */
  isPlatformActive: boolean;
  /** The app has its address (builds fail closed without it). */
  hasAddress: boolean;
  /** Every build of the app (the tab's read; the card takes its platform's slice). */
  builds: FactoryBuild[];
  /** The release feed (the tab's read; the card takes its platform's slice). */
  releases: FactoryRelease[];
  /** Every submission of the app (the tab's read; the card takes its store's slice). */
  submissions: FactorySubmission[];
  /** The store this platform's builds are submitted to. */
  store: FactoryStoreKey;
  /** The Farm chip is stale. */
  farmStale: boolean;
  /**
   * The factory's unmet prerequisites as customer sentences (build readiness,
   * 3.34.5; the Readiness card's rows); null = unknown, keep today's behaviour.
   */
  unmet: string[] | null;
}

/**
 * The Status tab's "Build the app" card (holder-requested builds, 3.34.0;
 * owner ruling 2026-09-23): the account holder asks the farm for a build of
 * the platform's current release. The farm is the authority — its claim is
 * idempotent (a click during a build answers the same row) and admission-
 * gated (unmet prerequisites come back as a refusal in the customer's words).
 * Since 3.34.1 the card also knows the store's build of the release: off while
 * one is built and waiting for the store, a caption once it is live (the farm
 * answers "nothing changed" unless images or settings moved — ADR-0033).
 * Since 3.34.5 the same admission is read before the click: the button is off
 * while the factory would refuse, with the first unmet item's sentence (the
 * Readiness card lists them all, each with where it is fixed).
 * An operator may pick another release; everyone else builds the current one.
 */
export function AppBuildRequestCard({
  appCfgId,
  platform,
  platformLabel,
  isAppActive,
  isPlatformActive,
  hasAddress,
  builds,
  releases,
  submissions,
  store,
  farmStale,
  unmet,
}: AppBuildRequestCardProps) {
  const isOperator = useIsFactoryOperator();
  const { supportContact } = useHelpTokens();
  const requestMutation = useRequestBuild(appCfgId);

  const platformReleases = useMemo(
    () => AppFactoryUtils.sortByCreatedDesc(AppFactoryUtils.releasesForPlatform(releases, platform)),
    [releases, platform],
  );
  const currentRelease = useMemo(
    () => AppBuildRequestUtils.currentRelease(releases, platform),
    [releases, platform],
  );
  // The operator's pick; null (or an id the feed no longer lists) = the current release.
  const [pickedId, setPickedId] = useState<string | null>(null);
  const release =
    (pickedId && platformReleases.find((r) => r.shellReleaseId === pickedId)) || currentRelease;

  const activeBuild = AppBuildRequestUtils.activeBuild(builds, platform);
  const storeBound = release
    ? AppBuildRequestUtils.storeBoundBuild(builds, submissions, platform, store, release.shellReleaseId)
    : null;
  const state = AppBuildRequestUtils.buttonState({
    appActive: isAppActive,
    platformActive: isPlatformActive,
    hasAddress,
    release,
    activeBuild,
    storeBound,
    farmStale,
    unmet,
  });
  const reason = state.reason?.replace('{platform}', platformLabel) ?? null;

  const handleBuild = () => {
    if (!release || requestMutation.isPending) return;
    const known = new Set(builds.map((b) => b.appBuild2Id));
    requestMutation.mutate(
      { platform, shellReleaseId: release.shellReleaseId },
      {
        onSuccess: (row) => {
          // The wire status is not exposed: `unchanged` is the farm's "the
          // store's build already has these inputs", a row we already listed
          // is its idempotent answer (in flight or resting), a new id is the
          // build we just queued.
          const outcome = AppBuildRequestUtils.outcome(row, known, release.semver);
          notifications.show({ title: outcome.title, message: outcome.message, color: outcome.color });
        },
        onError: (err) => {
          const message =
            err instanceof ApiError
              ? AppBuildRequestUtils.refusalSentence(err, platformLabel, supportContact)
              : err.message;
          notifications.show({ title: 'Build not started', message, color: 'red' });
        },
      },
    );
  };

  return (
    <Card withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <IconHammer size={28} color="var(--mantine-color-blue-6)" style={{ flexShrink: 0 }} />
          <Stack gap={4}>
            <Text fw={600}>Build the app</Text>
            <Text size="sm" c="dimmed">
              Builds version {release?.semver ?? '—'} of your {platformLabel} app with your current
              images and settings. When the checks pass, the store submission appears below.
            </Text>
            {isOperator && platformReleases.length > 1 && (
              <Select
                label="Release (operator)"
                size="xs"
                w={260}
                data={platformReleases.map((r) => ({
                  value: r.shellReleaseId,
                  label: `${r.semver} · lane ${r.lane}`,
                }))}
                value={release?.shellReleaseId ?? null}
                onChange={setPickedId}
                allowDeselect={false}
              />
            )}
          </Stack>
        </Group>
        <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
          <Button
            type="button"
            leftSection={<IconHammer size={16} />}
            onClick={handleBuild}
            disabled={state.disabled}
            loading={requestMutation.isPending}
          >
            Build app
          </Button>
          {reason && (
            <Text size="xs" c="dimmed" ta="right" maw={260}>
              {reason}
            </Text>
          )}
          {state.warning && (
            <Text size="xs" c="orange" ta="right" maw={260}>
              {state.warning}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
}
